import React from 'react'
import { CurvedEdge } from '../../../../types'
import { updatePolygonMeasurements } from '../../MapCanvas/helpers'
import { Point } from '../../MapCanvas/types'
import { PolygonPropertiesProps, CurvedEdgesPropertiesProps } from './types'
import {
  PropertySection,
  SectionHeader,
  SectionTitle,
  CollapseIcon,
  SectionContent,
  PropertyGroup,
  Label,
  Input,
  InfoText,
  Select,
  FlexRow,
  RangeInput,
  RangeValue,
  Button,
} from '../styled'
import {
  pixelsToReal,
  realToPixels,
  getUnitSingular,
  getUnitSquared,
  getDefaultCeilingHeight,
} from '../helpers'

const PolygonProperties: React.FC<PolygonPropertiesProps> = ({
  selectedElement,
  mapSettings,
  localProperties,
  isCollapsed,
  onToggleSection,
  updateProperty,
  updateMeasurement,
}) => {
  return (
    <PropertySection>
      <SectionHeader isCollapsed={isCollapsed} onClick={onToggleSection}>
        <SectionTitle>
          {selectedElement.type === 'polygon' ? 'Polygon' : 'Room'} Properties
        </SectionTitle>
        <CollapseIcon isCollapsed={isCollapsed}>▼</CollapseIcon>
      </SectionHeader>
      <SectionContent isCollapsed={isCollapsed}>
        <PropertyGroup>
          <Label>Room Name</Label>
          <Input
            value={localProperties.roomName || ''}
            onChange={e => updateProperty('roomName', e.target.value)}
            placeholder="e.g., Living Room, Kitchen"
          />
        </PropertyGroup>
        <PropertyGroup>
          <Label>Room Type</Label>
          <Select
            value={localProperties.roomType || 'other'}
            onChange={e => updateProperty('roomType', e.target.value)}>
            <option value="living-room">Living Room</option>
            <option value="kitchen">Kitchen</option>
            <option value="bedroom">Bedroom</option>
            <option value="bathroom">Bathroom</option>
            <option value="dining-room">Dining Room</option>
            <option value="office">Office/Study</option>
            <option value="closet">Closet</option>
            <option value="hallway">Hallway</option>
            <option value="garage">Garage</option>
            <option value="basement">Basement</option>
            <option value="other">Other</option>
          </Select>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Room Width ({getUnitSingular(mapSettings)})</Label>
          <Input
            type="number"
            step="0.1"
            value={(
              selectedElement.measurements?.realWidth ||
              pixelsToReal(localProperties.width || 0, mapSettings)
            ).toFixed(1)}
            onChange={e => {
              const realWidth = Number(e.target.value)
              updateMeasurement('realWidth', realWidth)
              updateProperty('width', realToPixels(realWidth, mapSettings))
            }}
          />
        </PropertyGroup>
        <PropertyGroup>
          <Label>Room Length ({getUnitSingular(mapSettings)})</Label>
          <Input
            type="number"
            step="0.1"
            value={(
              selectedElement.measurements?.realLength ||
              pixelsToReal(localProperties.height || 0, mapSettings)
            ).toFixed(1)}
            onChange={e => {
              const realLength = Number(e.target.value)
              updateMeasurement('realLength', realLength)
              updateProperty('height', realToPixels(realLength, mapSettings))
            }}
          />
        </PropertyGroup>
        <PropertyGroup>
          <Label>Ceiling Height ({getUnitSingular(mapSettings)})</Label>
          <Input
            type="number"
            step="0.1"
            value={(
              selectedElement.measurements?.realHeight ||
              getDefaultCeilingHeight(mapSettings)
            ).toFixed(1)}
            onChange={e =>
              updateMeasurement('realHeight', Number(e.target.value))
            }
          />
          <InfoText>
            Area:{' '}
            {selectedElement.type === 'polygon'
              ? (selectedElement.measurements?.realArea || 0).toFixed(1)
              : (
                  (selectedElement.measurements?.realWidth || 0) *
                  (selectedElement.measurements?.realLength || 0)
                ).toFixed(1)}{' '}
            {getUnitSquared(mapSettings)}
          </InfoText>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Floor Material</Label>
          <Select
            value={localProperties.floorMaterial || 'hardwood'}
            onChange={e => updateProperty('floorMaterial', e.target.value)}>
            <option value="hardwood">Hardwood</option>
            <option value="carpet">Carpet</option>
            <option value="tile">Tile</option>
            <option value="laminate">Laminate</option>
            <option value="vinyl">Vinyl</option>
            <option value="concrete">Concrete</option>
          </Select>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Label Font Size</Label>
          <FlexRow>
            <RangeInput
              min="8"
              max="48"
              value={localProperties.fontSize || 16}
              onChange={e =>
                updateProperty('fontSize', Number(e.target.value))
              }
            />
            <Input
              type="number"
              min="8"
              max="48"
              value={localProperties.fontSize || 16}
              onChange={e =>
                updateProperty('fontSize', Number(e.target.value))
              }
              style={{ width: '60px', marginLeft: '8px' }}
            />
            <RangeValue>{localProperties.fontSize || 16}px</RangeValue>
          </FlexRow>
        </PropertyGroup>
        <PropertyGroup>
          <Label>Label Rotation (degrees)</Label>
          <FlexRow>
            <RangeInput
              min="-180"
              max="180"
              value={localProperties.labelRotation ?? 0}
              onChange={e =>
                updateProperty('labelRotation', Number(e.target.value))
              }
            />
            <Input
              type="number"
              min="-180"
              max="180"
              value={localProperties.labelRotation ?? 0}
              onChange={e =>
                updateProperty('labelRotation', Number(e.target.value))
              }
              style={{ width: '80px', marginLeft: '8px' }}
            />
            <RangeValue>{localProperties.labelRotation ?? 0}°</RangeValue>
          </FlexRow>
        </PropertyGroup>
      </SectionContent>
    </PropertySection>
  )
}

const CurvedEdgesProperties: React.FC<CurvedEdgesPropertiesProps> = ({
  selectedElement,
  mapSettings,
  isCollapsed,
  onToggleSection,
  onUpdateElement,
}) => {
  if (selectedElement.type !== 'polygon') return null

  return (
    <PropertySection>
      <SectionHeader isCollapsed={isCollapsed} onClick={onToggleSection}>
        <SectionTitle>Curved Edges</SectionTitle>
        <CollapseIcon isCollapsed={isCollapsed}>▼</CollapseIcon>
      </SectionHeader>
      <SectionContent isCollapsed={isCollapsed}>
        <PropertyGroup>
          <InfoText
            style={{
              fontSize: '11px',
              color: '#656d76',
              marginBottom: '12px',
            }}>
            Create curved edges using Bézier curves for natural-looking
            architectural features like bay windows. Adjust curvature for
            different curve shapes.
          </InfoText>

          {(selectedElement.curvedEdges || []).map((edge, index) => (
            <div
              key={edge.edgeIndex}
              style={{
                marginBottom: '12px',
                padding: '8px',
                border: '1px solid #e1e5e9',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa',
              }}>
              <FlexRow style={{ marginBottom: '8px' }}>
                <Label style={{ margin: 0, fontSize: '11px' }}>
                  Edge {edge.edgeIndex}
                </Label>
                <Button
                  onClick={() => {
                    const newCurvedEdges = (
                      selectedElement.curvedEdges || []
                    ).filter(e => e.edgeIndex !== edge.edgeIndex)
                    const vertices = selectedElement.properties
                      .vertices as Point[]
                    const measurements = updatePolygonMeasurements(
                      vertices,
                      mapSettings,
                      newCurvedEdges,
                    )
                    onUpdateElement(selectedElement.id, {
                      curvedEdges: newCurvedEdges,
                      measurements: {
                        ...selectedElement.measurements,
                        ...measurements,
                      },
                    })
                  }}
                  style={{
                    background: '#dc3545',
                    color: 'white',
                    padding: '2px 6px',
                    fontSize: '10px',
                  }}>
                  Remove
                </Button>
              </FlexRow>
              <PropertyGroup>
                <Label style={{ fontSize: '10px' }}>Curvature</Label>
                <FlexRow>
                  <Input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={edge.curvature ?? 50}
                    onChange={event => {
                      const newCurvature = Number(event.target.value)
                      const vertices = selectedElement.properties.vertices || []

                      const newCurvedEdges = (
                        selectedElement.curvedEdges || []
                      ).map(e => {
                        if (e.edgeIndex === edge.edgeIndex) {
                          const startVertex = vertices[e.edgeIndex]
                          const endVertex =
                            vertices[(e.edgeIndex + 1) % vertices.length]

                          if (startVertex && endVertex) {
                            const dx = endVertex.x - startVertex.x
                            const dy = endVertex.y - startVertex.y
                            const perpX = -dy
                            const perpY = dx

                            const length = Math.sqrt(
                              perpX * perpX + perpY * perpY,
                            )
                            const normalizedPerpX = perpX / length
                            const normalizedPerpY = perpY / length

                            const curveDistance =
                              (newCurvature / 100) * (length * 0.5)
                            const offsetX = normalizedPerpX * curveDistance
                            const offsetY = normalizedPerpY * curveDistance

                            const newControlPoint1 = {
                              x: startVertex.x + dx * 0.33 + offsetX,
                              y: startVertex.y + dy * 0.33 + offsetY,
                            }

                            const newControlPoint2 = {
                              x: startVertex.x + dx * 0.66 + offsetX,
                              y: startVertex.y + dy * 0.66 + offsetY,
                            }

                            return {
                              ...e,
                              curvature: newCurvature,
                              controlPoint1: newControlPoint1,
                              controlPoint2: newControlPoint2,
                            }
                          }
                        }
                        return e
                      })

                      const polygonVertices =
                        selectedElement.properties.vertices
                      if (
                        polygonVertices &&
                        selectedElement.type === 'polygon'
                      ) {
                        const measurements = updatePolygonMeasurements(
                          polygonVertices as Point[],
                          mapSettings,
                          newCurvedEdges,
                        )
                        onUpdateElement(selectedElement.id, {
                          curvedEdges: newCurvedEdges,
                          measurements: {
                            ...selectedElement.measurements,
                            ...measurements,
                          },
                        })
                      }
                    }}
                    style={{ flex: 1, marginRight: '8px' }}
                  />
                  <span
                    style={{
                      fontSize: '10px',
                      minWidth: '40px',
                      textAlign: 'right',
                    }}>
                    {edge.curvature ?? 50}%
                  </span>
                </FlexRow>
                <InfoText
                  style={{ fontSize: '9px', color: '#888', marginTop: '4px' }}>
                  0% = straight line, 100% = maximum curve
                </InfoText>
              </PropertyGroup>
            </div>
          ))}

          <PropertyGroup>
            <Button
              onClick={() => {
                const vertices = (selectedElement.properties.vertices ||
                  []) as Point[]
                if (vertices.length < 3) return

                const existingEdges = selectedElement.curvedEdges || []
                const usedEdgeIndices = new Set(
                  existingEdges.map(e => e.edgeIndex),
                )

                let nextEdgeIndex = 0
                while (
                  usedEdgeIndices.has(nextEdgeIndex) &&
                  nextEdgeIndex < vertices.length
                ) {
                  nextEdgeIndex++
                }

                if (nextEdgeIndex < vertices.length) {
                  const startVertex = vertices[nextEdgeIndex]
                  const endVertex =
                    vertices[(nextEdgeIndex + 1) % vertices.length]

                  let controlPoint1 = { x: 0, y: 0 }
                  let controlPoint2 = { x: 0, y: 0 }

                  if (startVertex && endVertex) {
                    const dx = endVertex.x - startVertex.x
                    const dy = endVertex.y - startVertex.y
                    const perpX = -dy
                    const perpY = dx

                    const length = Math.sqrt(perpX * perpX + perpY * perpY)
                    const normalizedPerpX = perpX / length
                    const normalizedPerpY = perpY / length

                    const curveDistance = (50 / 100) * (length * 0.5)
                    const offsetX = normalizedPerpX * curveDistance
                    const offsetY = normalizedPerpY * curveDistance

                    controlPoint1 = {
                      x: startVertex.x + dx * 0.33 + offsetX,
                      y: startVertex.y + dy * 0.33 + offsetY,
                    }

                    controlPoint2 = {
                      x: startVertex.x + dx * 0.66 + offsetX,
                      y: startVertex.y + dy * 0.66 + offsetY,
                    }
                  }

                  const newCurvedEdge: CurvedEdge = {
                    edgeIndex: nextEdgeIndex,
                    curvature: 50,
                    controlPoint1: controlPoint1,
                    controlPoint2: controlPoint2,
                  }

                  const newCurvedEdges = [...existingEdges, newCurvedEdge]
                  const measurements = updatePolygonMeasurements(
                    vertices,
                    mapSettings,
                    newCurvedEdges,
                  )
                  onUpdateElement(selectedElement.id, {
                    curvedEdges: newCurvedEdges,
                    measurements: {
                      ...selectedElement.measurements,
                      ...measurements,
                    },
                  })
                }
              }}
              style={{
                background: '#28a745',
                color: 'white',
                padding: '6px 12px',
                fontSize: '11px',
              }}>
              Add Curved Edge
            </Button>
          </PropertyGroup>
        </PropertyGroup>
      </SectionContent>
    </PropertySection>
  )
}

export { PolygonProperties, CurvedEdgesProperties }
